import React from 'react';
import getComponentClassNames from '../../utils/className/getComponentClassNames';
import { Image } from '../../components/Image';
import { BlockLink, MaskedLink } from '../../components/Link';
import HeartIcon from '../../components/Image/HeartIcon';
import type { TLinkBase } from '../../components/Link/LinkBase';
import { Box } from '../../components/Box';
import { resolveFetchPriority, TFetchPriorityProps } from '../../components/Image/fetchPriority';
import ImageBadges, { TImageBadges } from './ImageBadges';
import Icon from '../Icon/Icon';
import { TIcon } from 'types/TStyleVariables';

const classNames = getComponentClassNames(['StructuredListing', { modifiers: 'vertical' }], ['body', 'title']);

type TDetails = {
  priorityProduct?: React.ReactNode;
  date?: React.ReactNode;
  location?: {
    label?: string;
    distance?: string;
  };
  internalLinkProps?: TLinkBase & { href: string; linkLabel?: string; iconName?: TIcon };
  externalLinkProps?: TLinkBase & { href: string; linkLabel?: string; iconName?: TIcon; ariaLabel?: string };
};

type TStructuredListingProps = {
  attributes?: { label: string; verifiedLabel?: string }[];
  title: string;
  price: string;
  imageSrc?: string;
  imageLoading?: 'lazy' | 'eager' | undefined;
  decoding?: 'sync' | 'async' | 'auto' | undefined;
  currency?: string;
  imageOverlayLabel?: string;
  className?: string;
  alt?: string;
  onError?: () => void;
  isMaskedLink?: boolean;
  onClick?: () => void;
  onContextMenu?: () => void;
  onHeart?: () => void;
  isFavorite?: boolean;
  showFav?: boolean;
  favAriaLabel?: string;
  linkProps?: TLinkBase & { href: string } & Record<string, any>;
  aspectRatio?: 'landscape' | 'portrait' | 'square';
  fit?: boolean;
  as?: 'article' | 'li';
  pricePrefix?: string;
  priceSuffix?: string;
  badges?: TImageBadges;
  details?: TDetails;
} & TFetchPriorityProps;

const StructuredListing = ({
  attributes,
  title,
  price,
  imageSrc = '',
  imageLoading,
  decoding,
  currency = '',
  isMaskedLink = false,
  onHeart,
  isFavorite = false,
  favAriaLabel = '',
  showFav = false,
  alt = 'image',
  className,
  linkProps = { href: '#' },
  aspectRatio = 'landscape',
  fit,
  badges,
  details,
  as = 'article',
  fetchPriority,
  fetchpriority,
  pricePrefix,
  priceSuffix,
}: TStructuredListingProps) => {
  const LinkComponent = isMaskedLink ? MaskedLink : BlockLink;
  const resolvedFetchPriority = resolveFetchPriority({ fetchPriority, fetchpriority }) ?? 'high';
  const { priorityProduct, location, date, externalLinkProps, internalLinkProps } = details || {};
  const defaultLinkAriaLabel = [
    title,
    currency ? `${currency} ${price}` : price,
    ...(attributes ?? []).flatMap(({ label, verifiedLabel }) => [label, verifiedLabel]),
  ]
    .filter(Boolean)
    .join(' — ');
  return (
    <Box
      as={as}
      name="StructuredListing"
      modifiers={attributes ? ['vertical', 'with-attributes'] : 'vertical'}
      className={className}
    >
      <LinkComponent aria-label={defaultLinkAriaLabel} {...linkProps}>
        <Image
          loading={imageLoading}
          decoding={decoding}
          src={imageSrc}
          alt={alt}
          fetchPriority={resolvedFetchPriority}
          aspectRatio={aspectRatio}
          fit={fit}
        >
          {badges && <ImageBadges badges={badges} />}
        </Image>
        <div className={classNames.body}>
          <span className={classNames.title}>{title}</span>
          <div className="hz-Title hz-Title--title5">
            {!!pricePrefix && <span className="hz-price-side-text">{pricePrefix}</span>}
            {currency && `${currency} `}
            {price}
            {!!priceSuffix && <span className="hz-price-side-text">{priceSuffix}</span>}
          </div>
          {attributes && (
            <div className="hz-StructuredListing-attributes">
              {attributes.map(({ label, verifiedLabel }) => (
                <span key={label}>
                  {label}
                  {verifiedLabel && (
                    <span role="img" aria-label={verifiedLabel} title={verifiedLabel}>
                      <Icon name="checkmark" color="success" />
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          {priorityProduct && <div className="hz-priority-product">{priorityProduct}</div>}
          {details && (
            <div className="hz-details">
              <div className="hz-Location-distance">
                {location?.label && <div>{location.label}</div>}
                {location?.distance && <div>{location.distance}</div>}
              </div>
              {date && <div>{date}</div>}
            </div>
          )}
        </div>
      </LinkComponent>
      {showFav ? (
        <HeartIcon onClick={onHeart} isFavorite={isFavorite} tabIndex="0" alwaysVisible ariaLabel={favAriaLabel} />
      ) : null}
      {details &&
        internalLinkProps &&
        (({ linkLabel, iconName, ...rest }) => (
          <MaskedLink {...rest}>
            <span className="hz-link-label">
              {iconName && <Icon name={iconName} color="text-link-default" />}
              {linkLabel}
            </span>
          </MaskedLink>
        ))(internalLinkProps)}
      {details &&
        externalLinkProps &&
        (({ linkLabel, iconName, ariaLabel = `${linkLabel}, opens in new tab`, ...rest }) => (
          <MaskedLink target="_blank" rel="noopener noreferrer nofollow" aria-label={ariaLabel} {...rest}>
            <span className="hz-link-label">
              {iconName && <Icon name={iconName} color="text-link-default" />}
              {linkLabel}
            </span>
          </MaskedLink>
        ))(externalLinkProps)}
    </Box>
  );
};

export default StructuredListing;
